// LIVE DATA — Performance › Software. Production health of the Revailo app,
// read from the workloads-production AWS account on every visit.
//
// The path: GET /api/software?window= → the sales stack's software-health
// lambda (terraform: modules/lambda/software_health.tf), which checks the
// caller's sign-in and workspace membership → production's read-only
// software-health-snapshot function (blueprints-terraform:
// software-health-snapshot.tf). What comes back is raw facts, snake_case, as
// production reads them:
//
//   health_check    the green health check (every 10 min, weekdays 07:00–18:50
//                   Chicago): the last 36 runs whole, and 30 days of each API's
//                   own lambda Invocations/Errors
//   traffic         api.revailo.com's router API — requests, 4xx, 5xx, p95/avg
//                   latency per bucket, plus the window and the window before
//   deployments     blue / green / router / health check / shared lambdas:
//                   invocations, errors, throttles, function count, last change
//   schedules       every scheduled job, each matched slot by slot against its
//                   target's real invocations
//   function_errors every lambda that errored in the window
//   environment     the blue/green SSM parameter, the API stage, the ASGs
//
// Everything below the fetch is pure: `shapeSnapshot` turns those facts into
// what the page draws, and scripts/test-softwareHealth.mjs pins it without a
// network. Nothing here is generated — a section production could not read
// arrives as null with a note, and the page says so rather than inventing it.
import { getIdToken } from './cognito.js'
import { shortDay, hourLabel, deltaOf } from './sampleData.js'

export const SOFTWARE_WINDOW_KEYS = ['24h', '7d', '30d']
export const SOFTWARE_WINDOW_LABELS = { '24h': 'Last 24 hours', '7d': 'Last 7 days', '30d': 'Last 30 days' }
export const DEFAULT_SOFTWARE_WINDOW = '24h'
export const isSoftwareWindow = (key) => SOFTWARE_WINDOW_KEYS.includes(key)

// Same escape hatch every other API module here has: `npm run start` without
// API_PROXY answers /api/* with index.html and a 200, so a non-JSON response
// means "no API on this origin" — retry the deployed stage directly.
const SOFTWARE_FALLBACK_BASE = 'https://hzpio22kcl.execute-api.us-east-1.amazonaws.com/prod'

/**
 * The production snapshot for one window. Throws an Error carrying `status`
 * (0 when the API could not be reached at all) and the server's own words, so
 * the page can tell an expired session (401) from a non-member (403) from
 * production being unreadable (502).
 */
export async function fetchSoftwareSnapshot({ window = DEFAULT_SOFTWARE_WINDOW } = {}) {
  const token = await getIdToken()
  const init = { headers: token ? { Authorization: `Bearer ${token}` } : {} }
  const query = `software?window=${encodeURIComponent(isSoftwareWindow(window) ? window : DEFAULT_SOFTWARE_WINDOW)}`
  const get = async (url) => {
    try {
      return await fetch(url, init)
    } catch {
      return null
    }
  }
  let res = await get(`/api/${query}`)
  const isJson = (r) => (r ? (r.headers.get('content-type') || '').includes('application/json') : false)
  if (!isJson(res)) res = await get(`${SOFTWARE_FALLBACK_BASE}/${query}`)
  if (!res) {
    const err = new Error('Production health could not be reached from this browser.')
    err.status = 0
    throw err
  }
  if (!isJson(res)) {
    const err = new Error(`Production health is unavailable (${res.status}).`)
    err.status = res.status
    throw err
  }
  const data = await res.json()
  if (!res.ok) {
    const err = new Error(data.error || `Production health failed (${res.status}).`)
    err.status = res.status
    throw err
  }
  return data
}

// ---- names ---------------------------------------------------------------------

// The health check's API keys are its test modules (testing/test_<api>.py).
const API_LABELS = {
  ai_release: 'AI release',
  announcement: 'Announcement',
  organization: 'Organization',
  software_release: 'Software release',
  user: 'User',
  project: 'Project',
  project_annotation: 'Project annotation',
  material_database: 'Material database',
  s3: 'S3 files',
  ocr: 'OCR',
  document: 'Document',
  document_annotation: 'Document annotation',
  page_annotation: 'Page annotation',
  utilities: 'Utilities',
  analysis: 'Analysis',
  chatgpt: 'ChatGPT',
}

export function apiLabel(api) {
  if (API_LABELS[api]) return API_LABELS[api]
  const words = String(api || '').replace(/[_-]+/g, ' ').trim()
  return words ? words[0].toUpperCase() + words.slice(1) : 'Unknown'
}

export const DEPLOYMENT_LABELS = {
  blue: 'Blue',
  green: 'Green',
  router: 'Router',
  health_check: 'Health check',
  shared: 'Shared',
}

// Which deployment a lambda belongs to — production's own rule (name prefix).
export function deploymentOf(functionName) {
  const name = String(functionName || '')
  if (name.startsWith('green-health-check-')) return 'health_check'
  for (const prefix of ['blue', 'green', 'router']) if (name.startsWith(`${prefix}-`)) return prefix
  return 'shared'
}

// ---- schedules -----------------------------------------------------------------

const DAY_WORDS = { SUN: 'Sun', MON: 'Mon', TUE: 'Tue', WED: 'Wed', THU: 'Thu', FRI: 'Fri', SAT: 'Sat' }
const pad2 = (n) => String(n).padStart(2, '0')

function zoneWord(timezone) {
  if (!timezone || timezone === 'UTC') return 'UTC'
  if (timezone === 'America/Chicago') return 'Chicago'
  return timezone
}

function dayWords(field) {
  if (field === '?' || field === '*') return null
  return field
    .split(',')
    .map((part) => part.split('-').map((d) => DAY_WORDS[d] || d).join('–'))
    .join(', ')
}

/**
 * An AWS schedule expression in words, for the forms production uses; anything
 * else comes back as the expression itself rather than a wrong paraphrase.
 *   cron(0/1 * * * ? *)                → Every minute
 *   cron(0/10 7-18 ? * MON-FRI *), Chi → Every 10 min, 07:00–18:50 Mon–Fri Chicago
 *   cron(59 23 * * ? *), Chicago       → Daily 23:59 Chicago
 *   cron(0 12 ? * MON-FRI *)           → Mon–Fri 12:00 UTC
 *   rate(5 minutes)                    → Every 5 minutes
 * Short on purpose: it sits in a table column beside four others.
 */
export function humanizeSchedule(expression, timezone) {
  const text = String(expression || '').trim()
  const rate = text.match(/^rate\((\d+)\s+(minute|minutes|hour|hours|day|days)\)$/)
  if (rate) return `Every ${rate[1]} ${rate[2]}`
  const cron = text.match(/^cron\((.+)\)$/)
  if (!cron) return text
  const fields = cron[1].trim().split(/\s+/)
  if (fields.length !== 6) return text
  const [minute, hour, dayOfMonth, month, dayOfWeek, year] = fields
  if (dayOfMonth !== '*' && dayOfMonth !== '?') return text
  if (month !== '*' || year !== '*') return text
  const days = dayWords(dayOfWeek)
  const zone = zoneWord(timezone)

  const step = minute.match(/^(?:0|\*)\/(\d+)$/)
  if ((step || minute === '*') && hour === '*') {
    const every = step ? Number(step[1]) : 1
    const base = every === 1 ? 'Every minute' : `Every ${every} min`
    return days ? `${base}, ${days}` : base
  }
  const hourRange = hour.match(/^(\d+)-(\d+)$/)
  if (step && hourRange) {
    const every = Number(step[1])
    const lastMinute = 60 - every
    const span = `${pad2(hourRange[1])}:00–${pad2(hourRange[2])}:${pad2(lastMinute)}`
    return `Every ${every === 1 ? 'minute' : `${every} min`}, ${span}${days ? ` ${days}` : ''} ${zone}`
  }
  if (/^\d+$/.test(minute) && /^\d+$/.test(hour)) {
    return `${days || 'Daily'} ${pad2(hour)}:${pad2(minute)} ${zone}`
  }
  return text
}

// ---- shaping -------------------------------------------------------------------

// A run's verdict on one API, in the notifier's own vocabulary
// (green-health-check notifier: _branch_status), mapped to the page's.
const RUN_STATE = { passed: 'ok', assertions_failed: 'degraded', infra_failed: 'down', unknown: 'unknown' }
const SERVICE_STATUS = { ok: 'operational', degraded: 'degraded', down: 'down', unknown: 'unknown' }
const STATUS_RANK = { operational: 0, unknown: 1, degraded: 2, down: 3 }
const RUN_STATE_WORD = { ok: 'passed', degraded: 'tests failed', down: 'could not run', unknown: 'no verdict', none: 'not run' }

// Jobs worth a person's attention, worst first.
const JOB_RANK = { missed: 0, failed: 1, no_target: 2, unknown: 3, on_time: 4, not_due: 5, disabled: 6 }

const ratio = (part, whole) => (whole ? part / whole : null)

function shapeServices(healthCheck) {
  if (!healthCheck) return []
  const runs = healthCheck.runs || [] // newest first, as production lists them
  const chronological = [...runs].reverse()
  return (healthCheck.active_apis || []).map((api) => {
    const history = chronological.map((run) => {
      const row = (run.apis || []).find((a) => a.api === api)
      const state = row ? RUN_STATE[row.kind] || 'unknown' : 'none'
      return { key: run.id, at: run.started_at, state, word: RUN_STATE_WORD[state] }
    })
    const latestRun = runs.find((run) => (run.apis || []).some((a) => a.api === api))
    const latest = latestRun ? latestRun.apis.find((a) => a.api === api) : null
    const checked = history.filter((h) => h.state !== 'none')
    const availability = (healthCheck.availability_30d || {})[api]
    return {
      key: api,
      name: apiLabel(api),
      status: latest ? SERVICE_STATUS[RUN_STATE[latest.kind] || 'unknown'] : 'unknown',
      lastCheckAt: latestRun ? latestRun.stopped_at || latestRun.started_at : null,
      durationSeconds: latest?.duration_seconds ?? null,
      tests: latest
        ? { passed: latest.tests_passed ?? null, failed: latest.tests_failed ?? null, skipped: latest.tests_skipped ?? null }
        : null,
      reason: latest?.reason || null,
      failures: latest?.failures || [],
      strip: history,
      passRate: checked.length ? checked.filter((h) => h.state === 'ok').length / checked.length : null,
      runsChecked: checked.length,
      availability30d:
        availability && availability.invocations ? 1 - availability.errors / availability.invocations : null,
      availabilityRuns: availability?.invocations ?? 0,
    }
  })
}

function bucketLabel(key, bucket) {
  return bucket === 'hour' ? hourLabel(new Date(key)) : shortDay(key)
}

function shapeSeries(traffic) {
  if (!traffic) return null
  const points = traffic.series || []
  const point = (b, value) => ({ key: b.key, label: bucketLabel(b.key, traffic.bucket), value })
  return {
    bucket: traffic.bucket,
    requests: points.map((b) => point(b, b.requests || 0)),
    errors: points.map((b) => point(b, b.errors_5xx || 0)),
    // A bucket with no traffic has no percentile; it draws as an empty bar.
    latency: points.map((b) => point(b, b.latency_p95_ms == null ? 0 : Math.round(b.latency_p95_ms))),
  }
}

function shapeDeployments(deployments, environment, serverGroups) {
  if (!deployments) return []
  const env = environment || {}
  const threshold = env.version_threshold
  return deployments.map((d) => {
    let role = ''
    if (d.name === env.current_deployment) role = threshold ? `Current · app versions before ${threshold}` : 'Current'
    else if (d.name === env.next_deployment) role = threshold ? `Next · app versions ${threshold} and later` : 'Next'
    else if (d.name === 'router') role = `Routes ${env.api_domain || 'the API'} to blue or green`
    else if (d.name === 'health_check') role = 'Tests green every 10 min on weekdays'
    else role = 'Used by both deployments'
    const servers = (serverGroups || []).filter((g) => g.deployment === d.name)
    return {
      key: d.name,
      name: DEPLOYMENT_LABELS[d.name] || d.name,
      role,
      functions: d.functions,
      invocations: d.invocations,
      invocationsDelta: deltaOf(d.invocations, d.prior_invocations, 'up'),
      errors: d.errors,
      errorRate: ratio(d.errors, d.invocations),
      throttles: d.throttles,
      lastCodeChange: d.last_code_change,
      servers,
    }
  })
}

function shapeJobs(schedules) {
  if (!schedules) return []
  return schedules
    .map((s) => ({
      key: `${s.name}:${s.target || 'none'}`,
      name: s.name,
      target: s.target || null,
      deployment: s.target ? deploymentOf(s.target) : null,
      source: s.source,
      schedule: humanizeSchedule(s.expression, s.timezone),
      expression: s.expression,
      status: s.status,
      lastRunAt: s.last_run_at || null,
      lastRunFailed: Boolean(s.last_run_failed),
      nextAt: s.status === 'disabled' || s.status === 'no_target' ? null : s.next_expected_at || null,
      invocations: s.invocations_in_window ?? null,
      errors: s.errors_in_window ?? null,
    }))
    .sort((a, b) => (JOB_RANK[a.status] ?? 9) - (JOB_RANK[b.status] ?? 9) || a.name.localeCompare(b.name))
}

function shapeErrors(functionErrors, healthCheck) {
  const rows = (functionErrors || []).map((f) => ({
    id: `fn:${f.function}`,
    level: 'error',
    source: f.function,
    deployment: f.deployment,
    kind: 'Lambda errors',
    message: `${f.errors.toLocaleString()} of ${f.invocations.toLocaleString()} invocations failed`,
    count: f.errors,
    firstSeenAt: f.first_error_hour,
    lastSeenAt: f.last_error_hour,
  }))

  // Health-check failures, one row per distinct failure across the recent runs.
  const grouped = new Map()
  for (const run of (healthCheck?.runs || [])) {
    for (const a of run.apis || []) {
      if (a.kind === 'passed') continue
      const entries =
        a.kind === 'infra_failed'
          ? [{ id: `hc:${a.api}:infra:${a.reason || ''}`, level: 'error', kind: 'Check could not run', message: a.reason || 'The test suite could not run.' }]
          : (a.failures || []).map((f) => ({
              id: `hc:${a.api}:${f.name}`,
              level: 'warn',
              kind: 'Test failure',
              message: f.detail ? `${f.name} — ${f.detail}` : f.name,
            }))
      for (const e of entries) {
        const hit = grouped.get(e.id)
        if (hit) {
          hit.count += 1
          if (run.started_at < hit.firstSeenAt) hit.firstSeenAt = run.started_at
          if (run.started_at > hit.lastSeenAt) hit.lastSeenAt = run.started_at
        } else {
          grouped.set(e.id, { ...e, source: `Health check · ${apiLabel(a.api)}`, deployment: 'green', count: 1, firstSeenAt: run.started_at, lastSeenAt: run.started_at })
        }
      }
    }
  }
  return [...rows, ...grouped.values()].sort((a, b) => String(b.lastSeenAt || '').localeCompare(String(a.lastSeenAt || '')))
}

function shapeEnvironment(environment, healthCheck) {
  if (!environment) return []
  const env = environment
  const rows = []
  if (env.current_deployment) {
    rows.push({
      key: 'current',
      label: 'Current deployment',
      value: DEPLOYMENT_LABELS[env.current_deployment] || env.current_deployment,
      note: env.version_threshold ? `app versions before ${env.version_threshold}` : '',
      at: env.deployment_settings_changed_at,
    })
  }
  if (env.next_deployment) {
    rows.push({
      key: 'next',
      label: 'Next deployment',
      value: DEPLOYMENT_LABELS[env.next_deployment] || env.next_deployment,
      note: env.version_threshold ? `app versions ${env.version_threshold} and later` : '',
      at: env.deployment_settings_changed_at,
    })
  }
  if (env.api_domain) {
    rows.push({ key: 'api', label: 'API', value: env.api_domain, note: env.api_stage ? `stage ${env.api_stage}` : '', at: env.api_last_deployed_at, atLabel: 'last deployed' })
  }
  for (const g of env.server_groups || []) {
    rows.push({
      key: `servers:${g.name}`,
      label: `${g.label} servers`,
      value: g.desired === 0 && g.max === 0 ? 'Scaled to zero' : `${g.in_service} of ${g.desired} in service`,
      note: `min ${g.min} · max ${g.max}${g.unhealthy ? ` · ${g.unhealthy} unhealthy` : ''}`,
    })
  }
  if (healthCheck) {
    rows.push({
      key: 'health-check',
      label: 'Health check',
      value: `${(healthCheck.active_apis || []).length} APIs against green`,
      note: 'every 10 min, weekdays 07:00–18:50 Chicago',
    })
  }
  if (env.region) rows.push({ key: 'region', label: 'Region', value: env.region, note: 'workloads-production account' })
  return rows
}

function worstStatus(services) {
  if (!services.length) return 'unknown'
  return services.reduce((worst, s) => ((STATUS_RANK[s.status] ?? 1) > (STATUS_RANK[worst] ?? 1) ? s.status : worst), 'operational')
}

/**
 * Production's raw snapshot → everything the Software page draws.
 */
export function shapeSnapshot(raw) {
  const healthCheck = raw?.health_check || null
  const traffic = raw?.traffic || null
  const environment = raw?.environment || null
  const services = shapeServices(healthCheck)
  const jobs = shapeJobs(raw?.schedules)
  const runs = healthCheck?.runs || []

  const totals = traffic?.totals || null
  const prior = traffic?.prior_totals || null
  const errorRate = totals ? ratio(totals.errors_5xx, totals.requests) : null
  const priorErrorRate = prior ? ratio(prior.errors_5xx, prior.requests) : null
  const availabilities = services.map((s) => s.availability30d).filter((v) => v != null)
  const watchedJobs = jobs.filter((j) => j.status !== 'disabled')
  const healthyJobs = watchedJobs.filter((j) => j.status === 'on_time' || j.status === 'not_due')

  const summary = {
    status: healthCheck ? worstStatus(services) : 'unknown',
    apisTotal: services.length,
    apisPassing: services.filter((s) => s.status === 'operational').length,
    problemApis: services.filter((s) => s.status !== 'operational').map((s) => s.name),
    lastRunAt: runs[0]?.stopped_at || runs[0]?.started_at || null,
    runningSince: healthCheck?.running_since || null,
    availability30d: availabilities.length ? availabilities.reduce((a, b) => a + b, 0) / availabilities.length : null,
    requests: totals?.requests ?? null,
    requestsDelta: totals && prior ? deltaOf(totals.requests, prior.requests, 'up') : null,
    errors5xx: totals?.errors_5xx ?? null,
    errors4xx: totals?.errors_4xx ?? null,
    errorRate,
    errorRateDelta: errorRate != null && priorErrorRate != null ? deltaOf(errorRate, priorErrorRate, 'down') : null,
    p95LatencyMs: totals?.latency_p95_ms ?? null,
    p95Delta: totals?.latency_p95_ms != null && prior?.latency_p95_ms != null ? deltaOf(totals.latency_p95_ms, prior.latency_p95_ms, 'down') : null,
    jobsTotal: watchedJobs.length,
    jobsHealthy: healthyJobs.length,
    problemJobs: watchedJobs.filter((j) => !healthyJobs.includes(j)).map((j) => j.name),
  }

  // What needs a person, in one list: APIs that are not passing, jobs that
  // missed or failed or can never run, and any part production could not read.
  const issues = []
  for (const s of services) {
    if (s.status === 'down') issues.push({ key: `api:${s.key}`, tone: 'danger', title: `${s.name} API`, detail: `could not be tested — ${s.reason || 'the check did not run'}`, at: s.lastCheckAt })
    else if (s.status === 'degraded') {
      const first = s.failures[0]
      const failed = s.tests?.failed
      issues.push({
        key: `api:${s.key}`,
        tone: 'warning',
        title: `${s.name} API`,
        detail: `${failed ?? 'some'} test${failed === 1 ? '' : 's'} failing${first ? ` — ${first.name}` : ''}`,
        at: s.lastCheckAt,
      })
    }
  }
  for (const j of jobs) {
    const where = j.target ? ` (${j.target})` : ''
    if (j.status === 'missed') issues.push({ key: `job:${j.key}`, tone: 'danger', title: j.name, detail: `missed its last run${where}`, at: j.lastRunAt })
    else if (j.status === 'failed') issues.push({ key: `job:${j.key}`, tone: 'warning', title: j.name, detail: `last run errored${where}`, at: j.lastRunAt })
    else if (j.status === 'no_target') issues.push({ key: `job:${j.key}`, tone: 'warning', title: j.name, detail: 'is enabled but has no target, so it never runs anything' })
  }
  for (const note of raw?.notes || []) issues.push({ key: `note:${note}`, tone: 'warning', title: 'Not read', detail: note })

  return {
    asOf: raw?.as_of || null,
    window: raw?.window || DEFAULT_SOFTWARE_WINDOW,
    windowStart: raw?.window_start || null,
    windowEnd: raw?.window_end || null,
    sections: {
      healthCheck: Boolean(healthCheck),
      traffic: Boolean(traffic),
      deployments: Boolean(raw?.deployments),
      jobs: Boolean(raw?.schedules),
      environment: Boolean(environment),
    },
    summary,
    issues,
    services,
    runsRead: runs.length,
    series: shapeSeries(traffic),
    deployments: shapeDeployments(raw?.deployments, environment, environment?.server_groups),
    jobs,
    errors: shapeErrors(raw?.function_errors, healthCheck),
    environment: shapeEnvironment(environment, healthCheck),
  }
}
